import React, { useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, Sector } from 'recharts';
import { MapPin, Truck, AlertTriangle, Activity, Shield, CheckCircle } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const RegionalOverview = ({ countries }) => {
  const [activePieIndex, setActivePieIndex] = useState({});
  const { theme, getThemeClasses } = useTheme();
  const themeClasses = getThemeClasses();
  // Calculate regional metrics
  const getRegionalMetrics = (country) => {
    const bases = country.bases;

    // Total vehicles per base
    const basesWithVehicleCounts = bases.map(base => {
      const readyVehicles = base.vehicles.filter(v => v.health >= 70 && v.alertLevel === 'low');
      const operationalReadiness = base.vehicles.length > 0
        ? ((readyVehicles.length / base.vehicles.length) * 100).toFixed(1)
        : 0;

      return {
        name: base.name,
        vehicleCount: base.vehicles.length,
        alerts: base.alerts,
        avgHealth: (base.vehicles.reduce((sum, v) => sum + v.health, 0) / base.vehicles.length).toFixed(1),
        operationalReadiness: operationalReadiness
      };
    });

    // Total fleet count
    const totalFleetCount = bases.reduce((sum, base) => sum + base.vehicles.length, 0);

    // Total alerts
    const totalAlerts = bases.reduce((sum, base) => sum + base.alerts, 0);

    // Average fleet health across all bases
    const allVehicles = bases.flatMap(base => base.vehicles);
    const avgFleetHealth = allVehicles.length > 0
      ? (allVehicles.reduce((sum, v) => sum + v.health, 0) / allVehicles.length).toFixed(1)
      : 0;

    // Calculate operational readiness for the country
    const readyVehicles = allVehicles.filter(v => v.health >= 70 && v.alertLevel === 'low');
    const missionReadyCount = readyVehicles.length;
    const missionReadyPercentage = allVehicles.length > 0
      ? ((missionReadyCount / allVehicles.length) * 100).toFixed(1)
      : 0;
    const operationalReadiness = allVehicles.length > 0
      ? ((readyVehicles.length / allVehicles.length) * 100).toFixed(1)
      : 0;

    // 5-Year Quarterly Failure Trend Data (Q2 and Q4 for each year)
    const currentYear = 2025;
    const quarterlyTrends = [];
    for (let i = 4; i >= 0; i--) {
      const year = currentYear - i;
      // Q2 data
      quarterlyTrends.push({
        quarter: `${year} Q2`,
        failures: Math.floor(Math.random() * 15) + 5, // Random failures between 5-20
        maintenanceEvents: Math.floor(Math.random() * 25) + 10, // Random maintenance 10-35
      });
      // Q4 data
      quarterlyTrends.push({
        quarter: `${year} Q4`,
        failures: Math.floor(Math.random() * 15) + 5,
        maintenanceEvents: Math.floor(Math.random() * 25) + 10,
      });
    }

    // Fleet health by alert level
    const alertLevels = { low: 0, medium: 0, high: 0 };
    allVehicles.forEach(vehicle => {
      alertLevels[vehicle.alertLevel]++;
    });

    return {
      basesWithVehicleCounts,
      totalFleetCount,
      totalAlerts,
      avgFleetHealth,
      operationalReadiness,
      missionReadyCount,
      missionReadyPercentage,
      quarterlyTrends,
      alertLevels
    };
  };

  const ALERT_COLORS = {
    low: '#10b981',
    medium: '#f59e0b',
    high: '#ef4444',
  };

  const CustomAreaTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const tooltipBg = theme === 'light' ? 'bg-white border-2 border-gray-300 shadow-lg' : 'bg-gray-900 border-2 border-military-600';
      const textPrimary = theme === 'light' ? 'text-gray-900' : 'text-white';
      const textSecondary = theme === 'light' ? 'text-gray-600' : 'text-gray-300';
      
      return (
        <div className={`${tooltipBg} rounded-lg p-3 shadow-xl`}>
          <p className={`${textPrimary} font-bold text-sm mb-2`}>{label}</p>
          <div className="space-y-1">
            {payload.map((entry, index) => (
              <div key={index} className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded"
                  style={{ backgroundColor: entry.color }}
                />
                <span className={`${textSecondary} text-xs capitalize`}>{entry.name}:</span>
                <span className={`${textPrimary} font-semibold text-xs`}>{entry.value}</span>
              </div>
            ))}
          </div>
        </div>
      );
    }
    return null;
  };

  const CustomPieTooltip = ({ active, payload, data }) => {
    if (active && payload && payload.length) {
      const total = data.low + data.medium + data.high;
      const tooltipBg = theme === 'light' ? 'bg-white/95 border border-gray-300 shadow-lg' : 'bg-gray-800/90';
      const textPrimary = theme === 'light' ? 'text-gray-900' : 'text-white';
      const textSecondary = theme === 'light' ? 'text-gray-700' : 'text-gray-100';
      
      return (
        <div className={`${tooltipBg} rounded-lg p-4 backdrop-blur-sm`}>
          <p className={`${textPrimary} font-bold text-lg mb-2`} style={{ textShadow: 'none' }}>{payload[0].name}</p>
          <p className={`${textSecondary} text-base mb-1`} style={{ textShadow: 'none' }}>
            Vehicles: <span className={`${textPrimary} font-bold text-lg`}>{payload[0].value}</span>
          </p>
          <p className={`${textSecondary} text-base`} style={{ textShadow: 'none' }}>
            Percentage: <span className={`${textPrimary} font-bold text-lg`}>{((payload[0].value / total) * 100).toFixed(1)}%</span>
          </p>
        </div>
      );
    }
    return null;
  };

  const renderActivePieShape = (props) => {
    const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill, payload, percent, value } = props;
    const textColor = theme === 'light' ? '#1f2937' : '#ffffff';
    const secondaryTextColor = theme === 'light' ? '#6b7280' : '#9ca3af';
    const accentColor = theme === 'light' ? '#059669' : '#4ade80';

    return (
      <g>
        <text x={cx} y={cy - 10} dy={8} textAnchor="middle" fill={textColor} className="font-bold text-sm">
          {payload.name}
        </text>
        <text x={cx} y={cy + 10} dy={8} textAnchor="middle" fill={secondaryTextColor} className="text-xs">
          {`${value} vehicles`}
        </text>
        <text x={cx} y={cy + 25} dy={8} textAnchor="middle" fill={accentColor} className="text-xs font-semibold">
          {`${(percent * 100).toFixed(1)}%`}
        </text>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius + 15}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
          stroke="#ffffff"
          strokeWidth={2}
          style={{ filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))' }}
        />
      </g>
    );
  };

  return (
    <div>
      <h2 className={`text-2xl font-bold ${themeClasses.textPrimary} mb-4`}>Global Overview - By Region</h2>

      <div className="grid grid-cols-1 2xl:grid-cols-2 gap-4 lg:gap-6">
        {countries.map(country => {
          const metrics = getRegionalMetrics(country);

          const alertChartData = [
            { name: 'Low Alert', value: metrics.alertLevels.low, color: ALERT_COLORS.low },
            { name: 'Medium Alert', value: metrics.alertLevels.medium, color: ALERT_COLORS.medium },
            { name: 'High Alert', value: metrics.alertLevels.high, color: ALERT_COLORS.high },
          ];

          return (
            <div key={country.id} className={`${themeClasses.card} border ${themeClasses.border} rounded-xl p-4 shadow-md`}>
              <h3 className={`text-xl font-bold ${themeClasses.textPrimary} mb-4 border-b ${themeClasses.border} pb-2`}>
                {country.name}
              </h3>

              {/* Regional Summary Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 mb-4">
                <div className="bg-gradient-to-br from-blue-600/20 to-blue-700/20 border border-blue-500/30 rounded-lg p-3 hover:from-blue-600/30 hover:to-blue-700/30 hover:border-blue-400/50 hover:scale-105 hover:shadow-lg transform transition-all duration-200 cursor-pointer">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className={`${themeClasses.textMuted} text-sm font-medium mb-1`}>Total Operational Bases</p>
                      <h4 className={`text-2xl font-bold ${themeClasses.textPrimary}`}>{country.bases.length}</h4>
                    </div>
                    <div className="bg-gradient-to-br from-blue-600 to-blue-700 p-2 rounded-lg">
                      <MapPin size={18} className="text-white" />
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-green-600/20 to-green-700/20 border border-green-500/30 rounded-lg p-3 hover:from-green-600/30 hover:to-green-700/30 hover:border-green-400/50 hover:scale-105 hover:shadow-lg transform transition-all duration-200 cursor-pointer">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className={`${themeClasses.textMuted} text-sm font-medium mb-1`}>Total Fleet Count</p>
                      <h4 className={`text-2xl font-bold ${themeClasses.textPrimary}`}>{metrics.totalFleetCount}</h4>
                    </div>
                    <div className="bg-gradient-to-br from-green-600 to-green-700 p-2 rounded-lg">
                      <Truck size={18} className="text-white" />
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-emerald-600/20 to-emerald-700/20 border border-emerald-500/30 rounded-lg p-3 hover:from-emerald-600/30 hover:to-emerald-700/30 hover:border-emerald-400/50 hover:scale-105 hover:shadow-lg transform transition-all duration-200 cursor-pointer">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className={`${themeClasses.textMuted} text-sm font-medium mb-1`}>Mission Ready</p>
                      <h4 className={`text-2xl font-bold ${themeClasses.textPrimary}`}>{metrics.missionReadyCount}</h4>
                      <p className={`${themeClasses.textMuted} text-xs`}>{metrics.missionReadyPercentage}% of fleet</p>
                    </div>
                    <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 p-2 rounded-lg">
                      <CheckCircle size={18} className="text-white" />
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-amber-600/20 to-amber-700/20 border border-amber-500/30 rounded-lg p-3 hover:from-amber-600/30 hover:to-amber-700/30 hover:border-amber-400/50 hover:scale-105 hover:shadow-lg transform transition-all duration-200 cursor-pointer">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className={`${themeClasses.textMuted} text-sm font-medium mb-1`}>Total Active Alerts</p>
                      <h4 className={`text-2xl font-bold ${themeClasses.textPrimary}`}>{metrics.totalAlerts}</h4>
                    </div>
                    <div className="bg-gradient-to-br from-amber-600 to-amber-700 p-2 rounded-lg">
                      <AlertTriangle size={18} className="text-white" />
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-military-600/20 to-military-700/20 border border-military-500/30 rounded-lg p-3 hover:from-military-600/30 hover:to-military-700/30 hover:border-military-400/50 hover:scale-105 hover:shadow-lg transform transition-all duration-200 cursor-pointer">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className={`${themeClasses.textMuted} text-sm font-medium mb-1`}>Avg Fleet Health</p>
                      <h4 className={`text-2xl font-bold ${themeClasses.textPrimary}`}>{metrics.avgFleetHealth}%</h4>
                    </div>
                    <div className="bg-gradient-to-br from-military-600 to-military-700 p-2 rounded-lg">
                      <Activity size={18} className="text-white" />
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-purple-600/20 to-purple-700/20 border border-purple-500/30 rounded-lg p-3 hover:from-purple-600/30 hover:to-purple-700/30 hover:border-purple-400/50 hover:scale-105 hover:shadow-lg transform transition-all duration-200 cursor-pointer">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className={`${themeClasses.textMuted} text-sm font-medium mb-1`}>Operational Readiness</p>
                      <h4 className={`text-2xl font-bold ${themeClasses.textPrimary}`}>{metrics.operationalReadiness}%</h4>
                    </div>
                    <div className="bg-gradient-to-br from-purple-600 to-purple-700 p-2 rounded-lg">
                      <Shield size={18} className="text-white" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Base Details Table */}
              <div className={`mb-4 ${themeClasses.card} rounded-lg p-3 sm:p-4 lg:p-6 border ${themeClasses.border} overflow-x-auto shadow-sm`}>
                <h4 className={`text-base sm:text-lg font-semibold ${themeClasses.textPrimary} mb-3`}>Operational Bases Details</h4>
                <div className="space-y-0 min-w-[600px]">
                  {/* Header */}
                  <div className={`grid grid-cols-5 gap-2 sm:gap-4 border-b ${themeClasses.border} pb-2 mb-2`}>
                    <div className={`text-left ${themeClasses.textMuted} font-medium text-base px-4`}>Base Name</div>
                    <div className={`text-right ${themeClasses.textMuted} font-medium text-base px-4`}>Fleet Count</div>
                    <div className={`text-right ${themeClasses.textMuted} font-medium text-base px-4`}>Active Alerts</div>
                    <div className={`text-right ${themeClasses.textMuted} font-medium text-base px-4`}>Avg Health</div>
                    <div className={`text-right ${themeClasses.textMuted} font-medium text-base px-4`}>Readiness</div>
                  </div>

                  {/* Rows */}
                  {metrics.basesWithVehicleCounts.map((base, idx) => (
                    <div
                      key={idx}
                      className={`grid grid-cols-5 gap-2 sm:gap-4 py-1 border-b ${themeClasses.border} hover:bg-military-700/20 hover:shadow-lg hover:scale-102 transform transition-all duration-200 cursor-pointer rounded-lg px-2 -mx-2`}
                    >
                      <div className={`${themeClasses.textPrimary} font-medium text-sm px-2 whitespace-nowrap`}>{base.name}</div>
                      <div className={`${themeClasses.textSecondary} text-right text-sm px-2`}>{base.vehicleCount}</div>
                      <div className={`${themeClasses.textSecondary} text-right text-sm px-2`}>{base.alerts}</div>
                      <div className={`${themeClasses.textSecondary} text-right text-sm px-2`}>{base.avgHealth}%</div>
                      <div className={`${themeClasses.textSecondary} text-right text-sm px-2`}>{base.operationalReadiness}%</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Charts Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* 5-Year Failure Trend Analysis */}
                <div className={`${themeClasses.chartBackground} border ${themeClasses.border} rounded-lg p-3 hover:border-military-600/50 transition-all`}>
                  <h4 className={`text-xs sm:text-sm font-bold ${themeClasses.textPrimary} mb-4 sm:mb-8`}>5-Year Failure Trend Analysis</h4>
                  <ResponsiveContainer width="100%" height={220}>
                    <AreaChart
                      data={metrics.quarterlyTrends}
                      margin={{ top: 5, right: 20, left: -10, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={theme === 'light' ? '#d1d5db' : '#374151'} />
                      <XAxis
                        dataKey="quarter"
                        stroke={theme === 'light' ? '#6b7280' : '#9ca3af'}
                        tick={{ fontSize: 10, fill: theme === 'light' ? '#6b7280' : '#9ca3af' }}
                        angle={-45}
                        textAnchor="end"
                        height={60}
                      />
                      <YAxis
                        stroke={theme === 'light' ? '#6b7280' : '#9ca3af'}
                        tick={{ fontSize: 10, fill: theme === 'light' ? '#6b7280' : '#9ca3af' }}
                      />
                      <Tooltip content={<CustomAreaTooltip />} />
                      <Legend
                        wrapperStyle={{ fontSize: '11px', color: theme === 'light' ? '#6b7280' : '#9ca3af' }}
                        iconType="rect"
                      />
                      <Area
                        type="monotone"
                        dataKey="maintenanceEvents"
                        name="Maintenance Events"
                        stroke="#3b82f6"
                        fill="#3b82f6"
                        fillOpacity={0.6}
                        strokeWidth={2}
                      />
                      <Area
                        type="monotone"
                        dataKey="failures"
                        name="Failures"
                        stroke="#ef4444"
                        fill="#ef4444"
                        fillOpacity={0.6}
                        strokeWidth={2}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                {/* Fleet Health by Alert Level Pie Chart */}
                <div className={`${themeClasses.chartBackground} border ${themeClasses.border} rounded-lg p-3 hover:border-military-500/70 hover:scale-102 hover:shadow-xl transform transition-all duration-300 cursor-pointer`}>
                  <h4 className={`text-xs sm:text-sm font-bold ${themeClasses.textPrimary} mb-3 transition-colors duration-300 hover:text-military-600`}>Fleet Health by Alert Level</h4>
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie
                        data={alertChartData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={activePieIndex[country.id] === null || activePieIndex[country.id] === undefined ? ({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%` : false}
                        outerRadius={75}
                        fill="#8884d8"
                        dataKey="value"
                        onMouseEnter={(_, index) => setActivePieIndex({ ...activePieIndex, [country.id]: index })}
                        onMouseLeave={() => setActivePieIndex({ ...activePieIndex, [country.id]: null })}
                        activeIndex={activePieIndex[country.id]}
                        activeShape={renderActivePieShape}
                      >
                        {alertChartData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={entry.color}
                            opacity={activePieIndex[country.id] === null || activePieIndex[country.id] === undefined || activePieIndex[country.id] === index ? 1 : 0.3}
                            stroke={activePieIndex[country.id] === index ? "#ffffff" : "transparent"}
                            strokeWidth={activePieIndex[country.id] === index ? 3 : 0}
                            style={{
                              transition: 'all 0.3s ease',
                              filter: activePieIndex[country.id] === index ? 'brightness(1.2) drop-shadow(0 2px 4px rgba(0,0,0,0.4))' : 'none'
                            }}
                          />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomPieTooltip data={metrics.alertLevels} />} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RegionalOverview;
